// src/features/sucursales/context/SucursalOperativaContext.js
//
// "Sucursal operativa": dónde está trabajando el usuario en ESTA SESIÓN de
// navegador. Es puramente de UI/contexto en esta fase (4B): no modifica
// compras, ventas, inventario ni ninguna operación real todavía.
//
// No existe el concepto de "sucursal preferida" ni de default por usuario:
// usuario y sucursal son independientes. Cualquier persona puede trabajar
// hoy en Crespo y mañana en Paraná. Por eso la selección se guarda en
// sessionStorage (sobrevive un refresh de la sesión actual, pero nunca se
// recuerda entre logins): se pide de nuevo en cada sesión nueva, y el
// logout la limpia explícitamente (ver clearSucursalOperativa, usada desde
// features/auth/pages/Logout.jsx) para no depender únicamente de que el
// navegador eventualmente cierre la pestaña.
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { obtenerUsuario } from "../../auth/services/authServices";
import { apiListarSucursales } from "../api/services";

const SucursalOperativaContext = createContext(null);

const STORAGE_KEY = "sucursal_operativa_sesion_id";

export function clearSucursalOperativa() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function SucursalOperativaProvider({ children }) {
  const [autenticado, setAutenticado] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const user = await obtenerUsuario();
      if (!user || !user.id) {
        setAutenticado(false);
        setSucursales([]);
        setSucursalId(null);
        return;
      }
      setAutenticado(true);

      const rows = await apiListarSucursales({ status: "activa" });
      setSucursales(rows);

      let stored = null;
      try {
        stored = sessionStorage.getItem(STORAGE_KEY);
      } catch {}
      const storedId = stored ? Number(stored) : null;
      const sigueActiva = storedId && rows.some((s) => Number(s.id) === storedId);
      setSucursalId(sigueActiva ? storedId : null);
    } catch {
      setSucursales([]);
      setSucursalId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial y re-sincronización en cada cambio de ruta: esto último
  // cubre el caso de un logout seguido de un login nuevo dentro de la misma
  // sesión de React (sin recarga dura de página), donde el usuario
  // autenticado cambia pero este provider sigue montado.
  useEffect(() => {
    cargar();
    // eslint-disable-next-line
  }, [pathname]);

  const seleccionar = useCallback((id) => {
    setSucursalId(id);
    try {
      sessionStorage.setItem(STORAGE_KEY, String(id));
    } catch {}
  }, []);

  const sucursalActual = useMemo(
    () => sucursales.find((s) => Number(s.id) === Number(sucursalId)) || null,
    [sucursales, sucursalId]
  );

  const value = useMemo(
    () => ({
      loading,
      sucursales,
      sucursalActual,
      seleccionar,
      refrescar: cargar,
      necesitaSeleccion: !loading && autenticado && !sucursalActual,
    }),
    [loading, sucursales, sucursalActual, seleccionar, cargar, autenticado]
  );

  return (
    <SucursalOperativaContext.Provider value={value}>
      {children}
    </SucursalOperativaContext.Provider>
  );
}

SucursalOperativaProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useSucursalOperativa() {
  const ctx = useContext(SucursalOperativaContext);
  if (!ctx) {
    throw new Error("useSucursalOperativa debe usarse dentro de SucursalOperativaProvider");
  }
  return ctx;
}
